import React, { useState } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  Activity,
  Settings,
  MessageSquare,
  LogOut,
  Menu,
  X,
  Shield,
  Bell,
  Crown,
  ChevronLeft,
  BookOpen
} from 'lucide-react';

const AdminLayout: React.FC = () => {
  const { user, loading, isAdmin, isRootAdmin, logout } = useAdminAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  const navigation = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: LayoutDashboard,
      current: location.pathname === '/admin'
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      current: location.pathname.startsWith('/admin/users')
    },
    {
      name: 'Admins',
      href: '/admin/admins',
      icon: UserCheck,
      current: location.pathname === '/admin/admins',
      rootOnly: true
    },
    {
      name: 'Blogs',
      href: '/admin/blogs',
      icon: BookOpen,
      current: location.pathname === '/admin/blogs'
    },
    {
      name: 'Activity Logs',
      href: '/admin/logs',
      icon: Activity,
      current: location.pathname === '/admin/logs'
    },
    {
      name: 'Support',
      href: '/admin/support',
      icon: MessageSquare,
      current: location.pathname.startsWith('/admin/support')
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: Settings,
      current: location.pathname === '/admin/settings',
      rootOnly: true
    }
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="h-screen bg-gradient-to-br from-background via-background to-muted/10 text-foreground flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm"></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 bg-gradient-to-b from-card via-card to-card/95 border-r border-border/50 shadow-2xl transform transition-all duration-300 ease-in-out flex flex-col backdrop-blur-xl
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarCollapsed ? 'w-16' : 'w-64'}
        lg:translate-x-0 lg:static lg:inset-0
      `}>
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-border/30 flex-shrink-0 bg-gradient-to-r from-primary/5 to-accent/5">
          <div className="flex items-center space-x-3">
            <div className="h-9 w-9 bg-gradient-to-br from-primary via-primary to-accent rounded-xl flex items-center justify-center shadow-lg ring-2 ring-primary/20">
              <Shield className="h-5 w-5 text-primary-foreground drop-shadow-sm" />
            </div>
            {!sidebarCollapsed && (
              <div>
                <span className="text-lg font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                  Admin Panel
                </span>
                <p className="text-xs text-muted-foreground -mt-1">Control Center</p>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex p-2 text-muted-foreground hover:text-foreground hover:bg-accent/20 rounded-lg transition-all duration-200 hover:scale-105"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeft className={`h-4 w-4 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground hover:bg-accent/20 rounded-lg transition-all duration-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-hidden">
          <nav className="px-3 py-6 h-full overflow-y-auto scrollbar-hide">
            <div className="space-y-1">
              {navigation.map((item, index) => {
                // Hide root-only items for non-root admins
                if (item.rootOnly && !isRootAdmin) {
                  return null;
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-300 w-full relative overflow-hidden
                      ${item.current
                        ? 'bg-gradient-to-r from-primary/20 to-accent/10 text-primary shadow-lg border border-primary/20 scale-105'
                        : 'text-muted-foreground hover:bg-gradient-to-r hover:from-accent/10 hover:to-primary/5 hover:text-foreground hover:scale-105 hover:shadow-md'
                      }
                      ${sidebarCollapsed ? 'justify-center px-2 mx-1' : 'mx-0'}
                    `}
                    title={sidebarCollapsed ? item.name : ''}
                  >
                    {/* Background glow effect for active item */}
                    {item.current && (
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-accent/5 rounded-xl blur-sm"></div>
                    )}

                    <item.icon className={`
                      h-5 w-5 flex-shrink-0 relative z-10 transition-all duration-300
                      ${sidebarCollapsed ? '' : 'mr-3'}
                      ${item.current ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-foreground group-hover:scale-110'}
                    `} />

                    {!sidebarCollapsed && (
                      <span className="truncate relative z-10 font-medium">{item.name}</span>
                    )}

                    {/* Tooltip for collapsed state */}
                    {sidebarCollapsed && (
                      <div className="absolute left-full ml-3 px-3 py-2 bg-popover/95 backdrop-blur-sm text-popover-foreground text-sm rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap z-50 border border-border/50">
                        {item.name}
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover rotate-45 border-l border-b border-border/50"></div>
                      </div>
                    )}

                    {/* Active indicator */}
                    {item.current && (
                      <div className="absolute right-2 w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>

        {/* User info and logout - Fixed at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-border/30 bg-gradient-to-r from-muted/10 to-accent/5">
          {!sidebarCollapsed ? (
            <>
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0 relative">
                  <div className="h-11 w-11 bg-gradient-to-br from-primary via-primary to-accent rounded-full flex items-center justify-center shadow-lg ring-2 ring-primary/20">
                    <span className="text-sm font-semibold text-primary-foreground drop-shadow-sm">
                      {user.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="absolute -bottom-1 -right-1 h-4 w-4 bg-green-500 rounded-full border-2 border-background flex items-center justify-center">
                    <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {user.displayName || user.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center mt-1">
                    {isRootAdmin ? (
                      <>
                        <Crown className="h-3 w-3 mr-1 text-accent" />
                        <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent font-medium">
                          Root Administrator
                        </span>
                      </>
                    ) : (
                      <>
                        <Shield className="h-3 w-3 mr-1 text-primary" />
                        <span className="text-primary font-medium">Administrator</span>
                      </>
                    )}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="w-full justify-start text-muted-foreground hover:text-foreground border-border/50 hover:bg-gradient-to-r hover:from-red-500/10 hover:to-red-600/5 hover:border-red-500/30 transition-all duration-300 group"
              >
                <LogOut className="mr-2 h-4 w-4 group-hover:text-red-500 transition-colors" />
                Sign Out
              </Button>
            </>
          ) : (
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className="h-9 w-9 bg-gradient-to-br from-primary via-primary to-accent rounded-full flex items-center justify-center shadow-lg ring-2 ring-primary/20">
                  <span className="text-xs font-semibold text-primary-foreground">
                    {user.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-500 rounded-full border border-background">
                  <div className="h-1.5 w-1.5 bg-green-400 rounded-full animate-pulse m-0.5"></div>
                </div>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="p-2 text-muted-foreground hover:text-foreground border-border/50 hover:bg-gradient-to-r hover:from-red-500/10 hover:to-red-600/5 hover:border-red-500/30 transition-all duration-300 group"
                title="Sign Out"
              >
                <LogOut className="h-4 w-4 group-hover:text-red-500 transition-colors" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header integrated into content */}
        <div className="flex-shrink-0 bg-background/95 backdrop-blur-md border-b border-border/50 shadow-sm">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-muted-foreground hover:text-foreground hover:bg-accent/10 rounded-lg transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>

              <div>
                <h1 className="text-xl font-semibold text-foreground">
                  {navigation.find(item => item.current)?.name || 'Dashboard'}
                </h1>
                <p className="text-xs text-muted-foreground">
                  {navigation.find(item => item.current)?.name === 'Dashboard'
                    ? 'Overview of your admin panel'
                    : `Manage ${navigation.find(item => item.current)?.name?.toLowerCase()}`
                  }
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // TODO: Implement notifications panel
                  toast.info('Notifications feature coming soon!');
                }}
                className="relative text-muted-foreground hover:text-foreground hover:bg-accent/10 h-9 w-9 p-0"
              >
                <Bell className="h-4 w-4" />
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
              </Button>

              <div className="hidden sm:flex items-center space-x-3 pl-3 border-l border-border/50">
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">
                    {user.displayName || user.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isRootAdmin ? 'Root Administrator' : 'Administrator'}
                  </p>
                </div>
                <div className="h-8 w-8 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center ring-2 ring-background shadow-lg">
                  <span className="text-xs font-semibold text-primary-foreground">
                    {user.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area - Scrollable */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-background via-background to-muted/10">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
